/// <amd-module name="Build/PipelinesLanguage/Utils/LanguageProviders" />

import { completionHelper, LanguageService, TextDocument, Thenable, YAMLDocument, YamlNodeInfo } from "AzurePipelinesLanguageService";
import { CodeLensOptions } from "Build/Common/Library/Contracts";
import * as Resources from "Build/PipelinesLanguage/Resources";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { createDocument, MonacoToProtocolConverter, ProtocolToMonacoConverter } from "./MonacoConverters";

export interface AzurePipelinesLanguageService extends LanguageService {
    parse(text: string): YAMLDocument;
}

type CompleteFunc = (languageService: AzurePipelinesLanguageService, model: monaco.editor.ITextModel, position: monaco.Position) => monaco.languages.CompletionList | Thenable<monaco.languages.CompletionList>;
const completeThrottleTimeoutMs = 500;

type FindLensesFunc = (model: monaco.editor.ITextModel) => monaco.languages.CodeLensList | Thenable<monaco.languages.CodeLensList>;
const findLensesThrottleTimeoutMs = 500;
const taskKey = "task";
const templateKey = "template";

export class AzurePipelinesCompletionProvider implements monaco.languages.CompletionItemProvider, monaco.IDisposable {
    private _timerManagement?: TimerManagement;
    private _completeThrottled: CompleteFunc;

    public triggerCharacters = [' '];

    constructor(private _languageService: AzurePipelinesLanguageService) {
        this._timerManagement = new TimerManagement();
        this._completeThrottled = this._timerManagement.throttle(this._doComplete, completeThrottleTimeoutMs, { leading: true, trailing: true }) as CompleteFunc;
    }

    public provideCompletionItems(model: monaco.editor.ITextModel, position: monaco.Position): monaco.languages.CompletionList | Thenable<monaco.languages.CompletionList> {
        return this._completeThrottled(this._languageService, model, position);
    }

    public resolveCompletionItem(item: monaco.languages.CompletionItem, token: monaco.CancellationToken): monaco.languages.CompletionItem | Thenable<monaco.languages.CompletionItem> {
        return this._languageService.doResolve(MonacoToProtocolConverter.asCompletionItem(item))
            .then(result => ProtocolToMonacoConverter.asCompletionItem(result));
    }

    public dispose(): void {
        this._timerManagement && this._timerManagement.dispose();
        this._timerManagement = undefined;
    }

    private _doComplete: CompleteFunc = (languageService, model, position) => {
        const textDocument: TextDocument = createDocument(model);
        const pos = MonacoToProtocolConverter.asPosition(position.lineNumber, position.column);
        if (!pos) {
            return {suggestions: [] };
        }

        const completionFix = completionHelper(textDocument, pos);
        const newText = completionFix.newText;
        const jsonDocument = this._languageService.parse(newText);

        return languageService.doComplete(textDocument, pos, jsonDocument)
            .then(list => ProtocolToMonacoConverter.asCompletionList(list));
    }
}

export class AzurePipelinesCodeLensProvider implements monaco.languages.CodeLensProvider, monaco.IDisposable {
    private _timerManagement?: TimerManagement;
    private _findLensesThrottled: FindLensesFunc;
    private _editTaskCommandId: string;
    private _openTemplateCommandId: string;
    private _validateCommandId: string;

    // Next fields are required to avoid circular references in this object.
    // That's a requirement since Monaco 0.22.3
    // Offending change: new this._getCodeLensModelDelays.get(model) call in https://github.com/microsoft/vscode/commit/89a2e78f7c43a7ea377e0d6a4a0e5ad81f326e38
    // Important: This is VERY FRAGILE. While it's not fixed in Monaco,
    // adding new fields here may break functionality if they contain circular references (like most DOM elements use to).
    private getModel: () => monaco.editor.ITextModel;
    private setSelection: (selection: monaco.IRange) => void;
    private startTask: (name: string, version: string, parameters: {}) => void;

    constructor(private languageService: AzurePipelinesLanguageService,
                pageContext: IVssPageContext,
                editor: monaco.editor.IStandaloneCodeEditor,
                private codeLensOptions?: CodeLensOptions) {
        this._timerManagement = new TimerManagement();
        this._findLensesThrottled = this._timerManagement.throttle(this.findLenses, findLensesThrottleTimeoutMs, { leading: true, trailing: true }) as FindLensesFunc;

        this._editTaskCommandId = editor.addCommand(0, this._findAndStartTask, "")!;

        if(this.codeLensOptions?.onOpenTemplate) {
            this._openTemplateCommandId = editor.addCommand(0, this._viewTemplate, "")!;
        }

        if(this.codeLensOptions?.onValidate) {
            this._validateCommandId = editor.addCommand(0, this._validateYaml, "")!;
        }

        this.getModel = () => editor.getModel()!;
        this.setSelection = (selection: monaco.IRange) => editor.setSelection(selection);
        this.startTask = (name, version, parameters) => {
            const taskService = pageContext.getService<any>("YamlAssistantViewService");
            taskService?.onStartTask(name, version, parameters);
        };
    }

    public provideCodeLenses(model: monaco.editor.ITextModel) {
        return this._findLensesThrottled(model);
    }

    public dispose(): void {
        this._timerManagement && this._timerManagement.dispose();
        this._timerManagement = undefined;
    }

    public findLenses: FindLensesFunc = async (model) => {
        const document = createDocument(model);
        const yamlDocument = this.languageService.parse(document.getText());

        const tasksPromise = this.findTasks(document, yamlDocument);
        const templatesPromise = this.codeLensOptions?.onOpenTemplate && this.codeLensOptions?.checkTemplate ? this.findTemplates(document, yamlDocument) : Promise.resolve([]);

        const tasks = await tasksPromise;
        const templates = await templatesPromise;

        return { lenses: [...tasks.lenses, ...templates], dispose: () => {} };
    }

    private findTasks(document: TextDocument, yamlDocument: YAMLDocument) {
        return this.languageService.findNodes && this.languageService.findNodes(document, yamlDocument, taskKey).then(taskNodes => {
            return taskNodes.reduce((result: monaco.languages.CodeLensList, task: YamlNodeInfo) => {
                const splitNameAndVersion = task.value && task.value.split("@");
                // only show the codelens once we have both a task name and version number
                if (splitNameAndVersion && splitNameAndVersion.length === 2 && /^\d+$/.test(splitNameAndVersion[1])) {
                    const startLineNumber = ProtocolToMonacoConverter.asPosition(task.startPosition)!.lineNumber;
                    let endLineNumber = ProtocolToMonacoConverter.asPosition(task.endPosition)!.lineNumber;

                    // if the task's last input is open ended, such as a multine line string using |
                    // the parser can't tell where the task has stopped and includes the next blank line as 'possible' input
                    // Since in this context we know that we're explicitly dealing in AzDev task inputs, 
                    // we can correct for that and not select (and thus later write over) this blank line below the task
                    let endLine = this.getModel().getLineContent(endLineNumber);
                    while (endLineNumber > startLineNumber && !/\S/.test(endLine)) {
                        endLineNumber--;
                        endLine = this.getModel().getLineContent(endLineNumber);
                    }

                    const endColumn = this.getModel().getLineMaxColumn(endLineNumber);

                    const range = {
                        startLineNumber: startLineNumber,
                        endLineNumber: endLineNumber,
                        startColumn: 1,
                        endColumn: endColumn
                    };
    
                    result.lenses.push({
                        range,
                        id: startLineNumber.toString(),
                        command: {
                            title: Resources.CodelensSettings,
                            arguments: [document, yamlDocument, task, range],
                            id: this._editTaskCommandId
                        }
                    } as monaco.languages.CodeLens);
                }

                return result;
            }, { lenses: [], dispose: () => {}});
        });
    }

    private async findTemplates(document: TextDocument, yamlDocument: YAMLDocument) {
        const lenses: monaco.languages.CodeLens[] = [];
        const templateNodes = await this.languageService.findNodes(document, yamlDocument, templateKey);
        for (const template of templateNodes) {
            const startPosition = ProtocolToMonacoConverter.asPosition(template.startPosition)!;
            const trimmedTemplate = template.value?.trim();
            // Don't create the lens if no template is actually named
            if(trimmedTemplate) {
                let templateExists = this.codeLensOptions!.checkTemplate!(trimmedTemplate);

                // Offer to re-validate if parsing failed
                if(templateExists === undefined) {
                    lenses.push({
                        command: {
                            title: Resources.CodelensValidateTemplate,
                            arguments: [],
                            id: this._validateCommandId
                        },
                        id: startPosition.lineNumber.toFixed(),
                        range: {
                            startLineNumber: startPosition.lineNumber,
                            startColumn: startPosition.column,
                            endLineNumber: startPosition.lineNumber,
                            endColumn: startPosition.column + template.key.length,
                        },
                    } as monaco.languages.CodeLens);
                }
                else {
                    lenses.push({
                        command: { title: templateExists ? Resources.CodelensViewTemplate : Resources.CodelensCreateTemplate, arguments: [template.value], id: this._openTemplateCommandId },
                        id: startPosition.lineNumber.toFixed(),
                        range: {
                            startLineNumber: startPosition.lineNumber,
                            startColumn: startPosition.column,
                            endLineNumber: startPosition.lineNumber,
                            endColumn: startPosition.column + template.key.length,
                        },
                    } as monaco.languages.CodeLens);
                }
            }
        }

        return lenses;
    }
    
    private _findAndStartTask = (unused: any, document: TextDocument, yamlDocument: YAMLDocument, taskNode: YamlNodeInfo, selectionRange: monaco.IRange) => {
        this.setSelection(selectionRange);

        // tell the editor to open the panel if its closed
        this.codeLensOptions?.onEditTask?.();

        // fill the form with the parsed yaml
        const params = this.languageService.getNodePropertyValues(document, yamlDocument, taskNode.startPosition, "inputs");
        const splitNameAndVersion = taskNode.value.split("@");
        this.startTask(splitNameAndVersion[0], splitNameAndVersion[1], params && params.values);
    }

    private _viewTemplate = (unused: any, template: string) => {
        this.codeLensOptions?.onOpenTemplate?.(template);
    }

    private _validateYaml = (unused: any) => {
        this.codeLensOptions?.onValidate?.();
    }
}

export const completionProvider = (languageService: AzurePipelinesLanguageService): AzurePipelinesCompletionProvider => {
    return new AzurePipelinesCompletionProvider(languageService);
}

export const codeLensProvider = (languageService: AzurePipelinesLanguageService,
    pageContext: IVssPageContext,
    editor: monaco.editor.IStandaloneCodeEditor,
    codeLensOptions?: CodeLensOptions): AzurePipelinesCodeLensProvider => {
    return new AzurePipelinesCodeLensProvider(languageService, pageContext, editor, codeLensOptions);
}

export const rangeFormattingEditProvider = (laguageService: LanguageService): monaco.languages.DocumentRangeFormattingEditProvider => ({
    provideDocumentRangeFormattingEdits(model, range, options, token): monaco.languages.TextEdit[] | Thenable<monaco.languages.TextEdit[]> {
        const document = createDocument(model);
        const edits = laguageService.doFormat(document, MonacoToProtocolConverter.asFormattingOptions(options), []);
        return ProtocolToMonacoConverter.asTextEdits(edits) || [];
    }
});

export const documentSymbolProvider = (languageService: AzurePipelinesLanguageService): monaco.languages.DocumentSymbolProvider => ({
    provideDocumentSymbols(model, token): monaco.languages.DocumentSymbol[] | Thenable<monaco.languages.DocumentSymbol[]> {
        const document = createDocument(model);
        const yamlDocument = languageService.parse(document.getText());
        return ProtocolToMonacoConverter.asSymbolInformations(languageService.findDocumentSymbols(document, yamlDocument)) || [];
    }
});

export const hoverProvider = (languageService: AzurePipelinesLanguageService): monaco.languages.HoverProvider => ({
    provideHover(model, position, token): monaco.languages.Hover | Thenable<monaco.languages.Hover> {
        const document = createDocument(model);
        const yamlDocument = languageService.parse(document.getText());
        const monacoPosition = MonacoToProtocolConverter.asPosition(position.lineNumber, position.column);
        return languageService.doHover(document, monacoPosition, yamlDocument).then((hover) => {
            return ProtocolToMonacoConverter.asHover(hover)!;
        });
    }
});

export const schemaResolver = (url: string): Promise<string> => {
    return Promise.resolve(url);
}
